/**
 * The account entries in the identity menu — "Profile" and "Change password" (bata-ui#132
 * round 2 and 2b, ADRs 9011 and 9012), and "User management" for an administrator (ADR 9014).
 *
 * One component for both rails, so the wide rail's menu and the collapsed rail's say the same
 * thing in the same words. «User activity» (ADR 9059) sits beside «User management», for the same
 * administrators. They sit INSIDE the identity menu and not beside sign-out: both are
 * rare, deliberate acts, and one press on the rail's face would put them on the same footing as
 * leaving.
 *
 * «Report triage» is offered to the delivery team only (the `feedback_triage` role), with the count of
 * reports waiting beside it. «Report a problem» (bata-platform#686, ADR 9063) opens the report form in its drawer, and «My
 * reports» lists what the person has already reported. Both are here, and so in the collapsed rail's
 * copy of this menu too. The first is drawn only where the form is mounted (`useFeedback`).
 *
 * "Profile" navigates inside the application and closes the menu it was chosen from — a menu
 * left open over the page it opened would cover the page. "Change password" leaves for
 * Keycloak's own page and comes back (`changePassword()` in `src/auth/session.ts`); the hint says
 * so before the press, because a navigation away from an unsaved screen should never be a
 * surprise.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixPopover from '@radix-ui/react-popover';
import { Activity, ClipboardCheck, KeyRound, ListChecks, MessageSquareWarning, UserRound, UsersRound } from 'lucide-react';
import { Button } from '../../primitives';
import { changePassword } from '../../auth/session';
import { ADMIN_USERS_PATH, USER_ACTIVITY_PATH } from '../routes/admin/constants';
import { FEEDBACK_PATH, FEEDBACK_TRIAGE_PATH } from '../routes/feedback/constants';
import { PROFILE_PATH } from '../routes/profile/ProfileScreen';
import { useShellAccess } from '../feedback/access';
import { useFeedback } from '../feedback/FeedbackProvider';
import { MyReportsBadge, TriageBadge } from '../feedback/TriageBadge';

export function AccountEntries({ onNavigate }: {
  readonly onNavigate: (to: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const hintId = useId();
  // User Management (ADR 9014) is offered only to someone its route would admit. A person who
  // cannot open it is not shown an entry that leads to "you do not have access".
  const { administers, triages } = useShellAccess();
  const feedback = useFeedback();
  return (
    <div className="border-hairline flex flex-col items-start gap-1 border-t pt-3">
      <RadixPopover.Close asChild>
        <Button variant="ghost" size="sm" icon={<UserRound size={13} aria-hidden />} onClick={() => onNavigate(PROFILE_PATH)}>
          {t('nav.profile')}
        </Button>
      </RadixPopover.Close>
      {feedback !== null && (
        <RadixPopover.Close asChild>
          <Button variant="ghost" size="sm" icon={<MessageSquareWarning size={13} aria-hidden />} onClick={feedback.openFromMenu}>
            {t('feedback.report.open')}
          </Button>
        </RadixPopover.Close>
      )}
      <RadixPopover.Close asChild>
        <Button variant="ghost" size="sm" icon={<ListChecks size={13} aria-hidden />} onClick={() => onNavigate(FEEDBACK_PATH)}>
          {t('nav.myReports')}
          <MyReportsBadge />
        </Button>
      </RadixPopover.Close>
      {triages && (
        <RadixPopover.Close asChild>
          <Button variant="ghost" size="sm" icon={<ClipboardCheck size={13} aria-hidden />} onClick={() => onNavigate(FEEDBACK_TRIAGE_PATH)}>
            {t('nav.feedbackTriage')}
            <TriageBadge />
          </Button>
        </RadixPopover.Close>
      )}
      {administers && (
        <RadixPopover.Close asChild>
          <Button variant="ghost" size="sm" icon={<UsersRound size={13} aria-hidden />} onClick={() => onNavigate(ADMIN_USERS_PATH)}>
            {t('nav.userManagement')}
          </Button>
        </RadixPopover.Close>
      )}
      {administers && (
        <RadixPopover.Close asChild>
          <Button variant="ghost" size="sm" icon={<Activity size={13} aria-hidden />} onClick={() => onNavigate(USER_ACTIVITY_PATH)}>
            {t('nav.userActivity')}
          </Button>
        </RadixPopover.Close>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon={<KeyRound size={13} aria-hidden />}
        onClick={changePassword}
        aria-describedby={hintId}
      >
        {t('nav.changePassword')}
      </Button>
      <p id={hintId} className="text-caption text-ink-secondary">{t('nav.changePasswordHint')}</p>
    </div>
  );
}
